import { Carousel } from '@mantine/carousel'
import { Box, Stack, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useConstants, useErrorMessages } from '../../constants/content'
import useSessionStorage from '../../hooks/useSessionStorage'
import {
  DISCOVER_CAROUSEL_CONTENT_TYPE,
  Entry,
  getParagraph,
  RichText,
} from '../../plugins/contentful/api'
import client from '../../plugins/contentful/client'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'
import GalleryCard, { GalleryElement } from './GalleryCard'
import SpacesCards from './SpacesCards'

/* -------------------------------------------------------------------------- */
/*                     Export Functions for Gallery Cards                     */
/* -------------------------------------------------------------------------- */
export type GalleryElementsImagesByEntryId = {
  // undefined means that the image is not loaded yet
  // true means that the image is loading
  // false means that the failed to be loaded
  // string means that the image is loaded and this is its URL
  [entryId: string]: undefined | string | boolean
}

export const wrapData = (
  elem: Entry,
  imagesByEntryId: GalleryElementsImagesByEntryId
): GalleryElement => {
  const { title, buttonLink, signedOut, signedIn } = elem.fields
  const paragraph =
    elem.fields.description === undefined ? '' : getParagraph(elem.fields.description)
  const description =
    elem.fields.description === undefined ? '' : (elem.fields.description as RichText)

  const imageAsset = imagesByEntryId[elem.sys.id]

  return {
    id: elem.sys.id,
    title,
    paragraph,
    description,
    image: typeof imageAsset === 'string' ? imageAsset : undefined,
    buttonLink,
    buttonText: elem.fields.buttonText === undefined ? '' : elem.fields?.buttonText || undefined,
    signedOut: signedOut === undefined ? false : signedOut,
    signedIn: signedIn === undefined ? true : signedIn,
  }
}

export const pullGalleryImage = (
  content: Entry[],
  oldEntry: GalleryElementsImagesByEntryId,
  setState: React.Dispatch<React.SetStateAction<GalleryElementsImagesByEntryId>>
) => {
  async function getAssetsByIds(
    entryIdAssetIdPairs: [entryId: string, assetId: string][],
    saveAsset: (entryId: string, urlOrError: string | Error) => void
  ): Promise<void> {
    for (let i = 0; i < entryIdAssetIdPairs.length; i++) {
      const [entryId, assetId] = entryIdAssetIdPairs[i]
      try {
        // eslint-disable-next-line no-await-in-loop
        const asset = await client.getAsset(assetId)
        const url = asset?.fields?.file?.url
        if (!url) {
          console.warn(`Asset ${assetId} has no url`)
        } else {
          saveAsset(entryId, url)
        }
      } catch (e) {
        console.error('ASSET error:', e)
        saveAsset(entryId, new Error(`${e}`))
      }
    }
  }

  const newGalleryImageByEntry: GalleryElementsImagesByEntryId = { ...oldEntry }

  content.forEach(elem => {
    const entryId = elem.sys.id
    newGalleryImageByEntry[entryId] =
      newGalleryImageByEntry[entryId] ?? elem.fields?.image?.fields?.file?.url
  })

  const entryIdsAssetIdsPairsToRequest = Object.keys(newGalleryImageByEntry)
    // filter only by undefined, which means the entry's image is still not loaded nor loading
    .filter(entryId => newGalleryImageByEntry[entryId] === undefined)
    .map(entryId => {
      const entry = content.find(elem => elem.sys.id === entryId)
      const assetId = entry?.fields?.image?.sys?.id
      return [entryId, assetId] as [string, string | undefined]
    })
    // filter out undefined, which means the entry has no image or the entry is not found
    .filter((tuple): tuple is [string, string] => !!tuple[0] && !!tuple[1])

  entryIdsAssetIdsPairsToRequest.forEach(([entryId]) => {
    newGalleryImageByEntry[entryId] = true
  })

  getAssetsByIds(entryIdsAssetIdsPairsToRequest, (entryId, url) => {
    setState(prevGalleryImageByEntry => ({
      ...prevGalleryImageByEntry,
      [entryId]: url instanceof Error ? false : url,
    }))
  }).catch(console.error)

  return newGalleryImageByEntry
}

/* -------------------------------------------------------------------------- */
/*                                      -                                     */
/* -------------------------------------------------------------------------- */

const DiscoverCarousel: React.FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const constants = useConstants()
  const errorMessages = useErrorMessages()
  const [discoverContent, setDiscoverContent] = useSessionStorage<Entry[]>(
    `discoverContent-${language}`,
    []
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  useEffect(() => {
    if (!discoverContent.length) {
      setIsLoading(true)
      client
        .getEntries({ content_type: DISCOVER_CAROUSEL_CONTENT_TYPE, locale: language })
        .then((response: any) => {
          const sortedContent = response.items.sort(
            (a: Entry, b: Entry) => a.fields.order - b.fields.order
          )
          setDiscoverContent(sortedContent)
        })
        .catch((err: any) => {
          setError(err.message)
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [discoverContent, setDiscoverContent, t, language])

  const [galleryImageByEntry, setGalleryImageByEntry] = useState<GalleryElementsImagesByEntryId>({})

  useEffect(() => {
    setGalleryImageByEntry(oldGalleryImageByEntry =>
      pullGalleryImage(discoverContent, oldGalleryImageByEntry, setGalleryImageByEntry)
    )
  }, [discoverContent])

  return (
    <Stack style={{ gap: 'lg', marginTop: '32px', marginBottom: '100px' }}>
      <Title order={isMobile ? 3 : 2} style={{ textAlign: 'left' }}>
        {constants.DISCOVER_HEADER}
      </Title>
      {isLoading && <Loading />}
      {error && <ErrorMessage message={errorMessages.cmsConnection} details={error} />}
      {!isLoading && !error && (
        <Stack
          style={{
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Carousel
            slideSize={`${100 / (discoverContent.length - 1)}%`}
            previousControlIcon={<IconCaretLeftFilled />}
            nextControlIcon={<IconCaretRightFilled />}
            slidesToScroll="auto"
          >
            <SpacesCards />
            {discoverContent.slice(0, -2).map((elem, index) => (
              <Carousel.Slide key={`${elem}${String(index)}`}>
                <Box style={{ display: 'flex', justifyContent: 'center' }}>
                  <GalleryCard elem={wrapData(elem, galleryImageByEntry)} />
                </Box>
              </Carousel.Slide>
            ))}
          </Carousel>
        </Stack>
      )}
    </Stack>
  )
}

export default DiscoverCarousel
